"use client";
import { useRouter } from "next/navigation";
import { useEffect } from "react";

/**
 * Keeps the dashboard current without a page load.
 *
 * `router.refresh()` re-renders the server components and streams the new markup in,
 * which is why the table keeps its sort, its filter and its expanded rows - a reload
 * would throw all of that away. The data itself comes from the server-side cache, so a
 * refresh costs the backend nothing.
 *
 * Sixty seconds, against the warmer's two-minute TTL: refreshing faster would re-render
 * the same numbers several times over, since nothing new can arrive between warms.
 */
const INTERVAL_MS = 60_000;

export function AutoRefresh() {
  const router = useRouter();

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | null = null;

    const start = () => {
      timer ??= setInterval(() => router.refresh(), INTERVAL_MS);
    };
    const stop = () => {
      if (timer === null) return;
      clearInterval(timer);
      timer = null;
    };

    // A background tab refreshing on a timer is wasted work on both ends. Coming back
    // refreshes at once, because that is the moment someone looks at the numbers again.
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        router.refresh();
        start();
      } else {
        stop();
      }
    };

    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [router]);

  return null;
}
